import type { Score, Streak } from "../state/roundLoop";
import { HeaderStats } from "./AppHeader";
import { Tooltip } from "./Tooltip";

/**
 * The Predict stats (spec §9): score (hit rate), current/best Streak. It
 * renders inside the shared AppHeader row — which already says which
 * top-level mode (Predict/Trade) you are in via the switcher (GRA-94), so
 * this carries no repeat of that.
 *
 * The scope this score belongs to (mode + timeframe, GRA-128) used to be
 * printed as a label ahead of the fraction; that label, and the share
 * control this bar used to carry, both crowded this group into overlapping
 * the switcher's own segments at the header's narrower widths (GRA-155). The
 * scope reads from the fraction's tooltip now instead of taking up its own
 * words on the primary surface, and the share control moved to the session
 * panel (`HistoryPanel.tsx`) — one control per run, not the group repeated
 * per Loop's header. The random-guess/majority-direction benchmark that used
 * to ride the accuracy tooltip moved there too, next to the share control,
 * so this bar keeps only the numbers: fraction, percent, streak, best.
 */
export type StatusBarProps = {
  score: Score;
  streak: Streak;
  /**
   * Random-guess baseline (%) for this mode. Not shown as text (GRA-95); it
   * only decides whether the accuracy percentage highlights as beating
   * chance. The benchmark text itself lives in the session panel now.
   */
  baseline?: number;
  /**
   * Which mode + timeframe this score belongs to, e.g. "Trend · Daily"
   * (GRA-128). Read on the fraction's tooltip (GRA-155) rather than printed
   * as a label, so switching mode or timeframe still reads as a different
   * scoreboard rather than the score just having reset, without costing
   * space on the primary surface.
   */
  scopeLabel: string;
};

export function StatusBar({ score, streak, baseline, scopeLabel }: StatusBarProps) {
  const accuracy = score.played === 0 ? 0 : Math.round((score.correct / score.played) * 100);
  const beatsBaseline = baseline !== undefined && score.played > 0 && accuracy > baseline;

  return (
    <HeaderStats>
      <span>
        <Tooltip text={`${scopeLabel} · rounds correct / rounds played`} align="left">
          <span className="text-fg">{score.correct}</span>/{score.played}
        </Tooltip>
        {score.played > 0 && (
          <span className={`ml-1 ${beatsBaseline ? "text-green-400" : ""}`}>({accuracy}%)</span>
        )}
      </span>
      <span>
        <Tooltip text="consecutive correct guesses">streak</Tooltip>{" "}
        <span className="text-fg">{streak.current}</span>
        <span className="ml-2">
          <Tooltip text="longest streak on record">best</Tooltip> {streak.best}
        </span>
      </span>
    </HeaderStats>
  );
}
